/**
 * 購物車管理 - 刪除和數量調整功能
 */

class CartManager {
  constructor() {
    this.cartItems = new Map();
    this.init();
  }

  init() {
    this.loadCartFromPage();
    this.attachEventListeners();
    this.updateTotals();
  }

  loadCartFromPage() {
    const cartItemElements = document.querySelectorAll('.cart-item');
    cartItemElements.forEach(element => {
      const itemId = element.dataset.itemId;
      const itemKey = element.dataset.itemKey;
      const priceElement = element.querySelector('.item-price-text');
      
      if (priceElement) {
        // Shopify 以最小貨幣單位儲存價格（TWD 1699 → 169900 cents）
        // item.variant.price 輸出的是 cents，需除以 100 轉成元
        const rawPrice = parseFloat(priceElement.dataset.itemPrice) || 0;
        const price = rawPrice / 100;
        const quantity = parseInt(priceElement.dataset.itemQuantity) || 1;
        
        this.cartItems.set(itemKey, {
          id: itemId,
          key: itemKey,
          quantity: quantity,
          price: price,
          element: element
        });
      }
    });
  }

  attachEventListeners() {
    // 刪除按鈕
    document.querySelectorAll('.btn-delete-item').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleDeleteItem(e));
    });
  }

  handleQuantityChange(event = {}) {
    if (event.preventDefault) event.preventDefault();
    const btn = event.target || {};
    const action = btn.dataset?.action || '';
    const qtyInput = btn.closest('.quantity-selector')?.querySelector('.qty-input');
    let quantity = qtyInput ? parseInt(qtyInput.value) : 1;

    if (action === 'increase') {
      quantity++;
    } else if (action === 'decrease' && quantity > 1) {
      quantity--;
    }

    qtyInput.value = quantity;
    this.updateItemQuantity(qtyInput.closest('.cart-item'), quantity);
  }

  handleQuantityInput(event = {}) {
    const input = event.target || {};
    let quantity = parseInt(input.value || 1);

    if (isNaN(quantity) || quantity < 1) {
      quantity = 1;
      input.value = 1;
    }

    this.updateItemQuantity(input.closest('.cart-item'), quantity);
  }

  updateItemQuantity(cartItemElement = {}, quantity = 0) {
    const itemKey = cartItemElement.dataset?.itemKey || '';
    const item = this.cartItems.get(itemKey);

    if (item) {
      item.quantity = quantity;
      
      // 更新價格顯示
      const priceElement = cartItemElement.querySelector('.item-price-text');
      const totalPrice = item.price * quantity;
      priceElement.textContent = this.formatPrice(totalPrice);
      priceElement.dataset.itemQuantity = quantity;
    }

    this.updateTotals();
  }

  handleDeleteItem(event = {}) {
    if (event.preventDefault) event.preventDefault();
    const cartItemElement = event.target?.closest('.cart-item');
    const itemKey = cartItemElement?.dataset?.itemKey || '';

    if (confirm('確定要刪除此商品嗎？')) {
      this.cartItems.delete(itemKey);
      cartItemElement.remove();
      this.updateTotals();
      
      // 📊 Meta Pixel 追踪：购物车商品删除（异步，不阻塞主流程）
      if (window.metaPixelTracker && this.cartItems.size > 0) {
        const remainingItems = Array.from(this.cartItems.values()).map(item => ({
          productId: item.id,
          productTitle: item.title || '商品',
          quantity: item.quantity,
          price: item.price
        }));
        
        const subtotal = remainingItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        
        window.metaPixelTracker?.trackAddToCart({
          totalPrice: subtotal,
          items: remainingItems,
          customerEmail: this.getCustomerEmail()
        }).catch((err = {}) => console.warn('Meta追踪失败:', err.message || err));
      }
    }
  }

  updateTotals() {
    let subtotal = 0;
    let itemCount = 0;

    this.cartItems.forEach(item => {
      subtotal += item.price * item.quantity;
      itemCount += item.quantity;
    });

    // 更新小計顯示
    const subtotalElement = document.getElementById('subtotal');
    if (subtotalElement) {
      subtotalElement.textContent = this.formatPrice(subtotal);
    }

    // 🎯 Meta Pixel 追踪：更新购物车（异步，不阻塞主流程）
    if (window.metaPixelTracker && this.cartItems.size > 0) {
      // 使用 setTimeout 避免频繁调用
      if (this.metaTrackingTimeout) {
        clearTimeout(this.metaTrackingTimeout);
      }
      
      this.metaTrackingTimeout = setTimeout(() => {
        const cartItems = Array.from(this.cartItems.values()).map((item = {}) => ({
          productId: item.id,
          productTitle: item.title || '商品',
          quantity: item.quantity,
          price: item.price
        }));
        
        window.metaPixelTracker?.trackAddToCart({
          totalPrice: subtotal,
          items: cartItems,
          customerEmail: this.getCustomerEmail()
        }).catch((err = {}) => console.warn('Meta追踪失败:', err.message || err));
      }, 500); // 防抖：500ms 延迟
    }

    // 更新運費和總計（假設有相關的 JavaScript 處理）
    this.updateShippingAndTotal(subtotal);
  }
  
  // 獲取客戶郵箱（如果可用）
  getCustomerEmail() {
    const emailInput = document.querySelector('input[name="customer_email"]');
    return emailInput ? emailInput.value : '';
  }

  updateShippingAndTotal(subtotal = 0) {
    const shippingMethod = document.querySelector('input[name="shipping_method"]:checked')?.value || 'home_delivery';
    
    // 從 Liquid 模板中獲取運費配置
    const config = window.shippingConfig || {};
    const homeDeliveryFee = config?.homeDeliveryFee || 80;
    const storePickupFee = config?.storePickupFee || 60;
    const freeShippingThreshold = config?.freeShippingThreshold || 499;

    let shippingFee = 0;
    if (shippingMethod === 'home_delivery') {
      shippingFee = homeDeliveryFee;
    } else if (shippingMethod === 'store_pickup') {
      shippingFee = storePickupFee;
    }

    // 免運費邏輯
    if (subtotal >= freeShippingThreshold) {
      shippingFee = 0;
    }

    const total = subtotal + shippingFee;

    const shippingElement = document.getElementById('shipping-fee');
    const totalElement = document.getElementById('total-amount');

    if (shippingElement) {
      shippingElement.textContent = shippingFee > 0 ? this.formatPrice(shippingFee) : '免運';
    }

    if (totalElement) {
      totalElement.textContent = this.formatPrice(total);
    }
  }

  formatPrice(price = 0) {
    // 格式化為台幣，整數無小數
    return 'NT$' + Math.floor(price || 0).toLocaleString('en-US');
  }

  getCartData() {
    const items = [];
    this.cartItems.forEach((item = {}) => {
      items.push({
        key: item.key || '',
        quantity: item.quantity || 0,
        price: item.price || 0
      });
    });
    return {
      items: items,
      subtotal: this.calculateSubtotal()
    };
  }

  calculateSubtotal() {
    let total = 0;
    this.cartItems.forEach((item = {}) => {
      total += (item.price || 0) * (item.quantity || 0);
    });
    return total;
  }
}

// 初始化購物車管理器
let cartManager;
document.addEventListener('DOMContentLoaded', function() {
  cartManager = new CartManager();
});
